import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil, forkJoin } from 'rxjs';
import { DateTime } from 'luxon';
import { KsefMonitoringApiService } from 'shared/services/api/ksef-monitoring.api.service';

interface CronLog {
  _id: string;
  logType: string;
  status: string;
  timestamp: string;
  data: {
    cronType?: string;
    usersProcessed?: number;
    totalInvoices?: number;
    successCount?: number;
    failedCount?: number;
    skippedNoSubscription?: number;
    duration?: number;
    reason?: string;
    failedUsers?: { userId: string; userLabel: string; error: string }[];
  };
}

interface SyncConnection {
  userId: string;
  userEmail: string;
  companyName: string;
  nip: string;
  lastSyncAt: string | null;
  lastSyncDuration: string | null;
  lastSyncInvoicesCount: number;
  lastSyncError: string | null;
  lastSyncErrorAt: string | null;
  timeSinceLastSync: string;
  status: string;
  environment: string;
  isEligibleForCron: boolean;
  connectionType: string | null;
  permissions: string | string[] | null;
}

interface SyncSummary {
  totalConnections: number;
  neverSynced: number;
  withErrors: number;
  stale: number;
}

@Component({
  selector: 'app-sync-status',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    DatePipe
  ],
  templateUrl: './sync-status.component.html',
  styleUrls: ['./sync-status.component.scss']
})
export class SyncStatusComponent implements OnInit, OnDestroy {

  isLoading = true;
  isRefreshing = false;
  isTriggeringSync = false;
  syncTriggerResult: { triggered: boolean; reason: string | null; message: string } | null = null;

  cronLogs: CronLog[] = [];
  connections: SyncConnection[] = [];
  summary: SyncSummary | null = null;

  // Diagnozuj
  showDiagnosePanel = false;
  diagnoseNip = '';
  isDiagnosing = false;
  diagnoseLogLines: { level: string; text: string }[] = [];
  diagnoseSuccess: boolean | null = null;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(private _ksefApiService: KsefMonitoringApiService) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  loadData(): void {
    this.isLoading = !this.isRefreshing;
    this.isRefreshing = true;

    forkJoin({
      cronLogs: this._ksefApiService.getCronLogs(30),
      syncStatus: this._ksefApiService.getSyncStatus()
    })
    .pipe(takeUntil(this._unsubscribeAll))
    .subscribe({
      next: ({ cronLogs, syncStatus }) => {
        this.cronLogs = cronLogs.data?.logs || [];
        this.connections = syncStatus.data?.connections || [];
        this.summary = syncStatus.data?.summary || null;
        this.isLoading = false;
        this.isRefreshing = false;
      },
      error: (err) => {
        console.error('Błąd ładowania danych synchronizacji:', err);
        this.isLoading = false;
        this.isRefreshing = false;
      }
    });
  }

  refresh(): void {
    this.isRefreshing = true;
    this.loadData();
  }

  /**
   * Ręczne uruchomienie synchronizacji z KSeF
   */
  triggerManualSync(): void {
    this.isTriggeringSync = true;
    this.syncTriggerResult = null;

    this._ksefApiService.triggerManualSync()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (response) => {
          this.isTriggeringSync = false;
          this.syncTriggerResult = {
            triggered: response.data?.triggered ?? false,
            reason: response.data?.reason ?? null,
            message: response.message || ''
          };

          // Jeśli uruchomiono, odśwież dane po 3s żeby zobaczyć nowy log cron_started
          if (response.data?.triggered) {
            setTimeout(() => this.loadData(), 3000);
          }
        },
        error: (err) => {
          this.isTriggeringSync = false;
          this.syncTriggerResult = {
            triggered: false,
            reason: 'error',
            message: err.error?.message || 'Wystąpił błąd podczas uruchamiania synchronizacji'
          };
        }
      });
  }

  dismissSyncResult(): void {
    this.syncTriggerResult = null;
  }

  /**
   * Formatuj czas trwania (ms → czytelne)
   */
  formatDuration(ms: number | undefined): string {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }

  /**
   * Formatuj datę relatywnie
   */
  formatRelativeDate(dateStr: string): string {
    const dt = DateTime.fromISO(dateStr);
    if (!dt.isValid) return dateStr;
    const diff = DateTime.now().diff(dt, 'minutes').minutes;
    if (diff < 1) return 'przed chwilą';
    if (diff < 60) return `${Math.floor(diff)} min temu`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h temu`;
    return dt.toFormat('dd.MM.yyyy HH:mm');
  }

  /**
   * Ikona dla typu logu cron
   */
  getCronIcon(logType: string): string {
    switch (logType) {
      case 'cron_started': return 'tabler_outline:player-play';
      case 'cron_completed': return 'tabler_outline:circle-check';
      case 'cron_failed': return 'tabler_outline:alert-triangle';
      case 'cron_skipped': return 'tabler_outline:player-skip-forward';
      default: return 'tabler_outline:clock';
    }
  }

  /**
   * Kolor ikony dla typu logu
   */
  getCronIconClass(logType: string, status?: string): string {
    if (logType === 'cron_completed' && status === 'warning') return 'text-amber-600 dark:text-amber-400';
    switch (logType) {
      case 'cron_started': return 'text-blue-600 dark:text-blue-400';
      case 'cron_completed': return 'text-green-600 dark:text-green-400';
      case 'cron_failed': return 'text-red-600 dark:text-red-400';
      case 'cron_skipped': return 'text-gray-500 dark:text-gray-400';
      default: return 'text-gray-600';
    }
  }

  /**
   * Label dla typu logu
   */
  getCronLabel(logType: string): string {
    switch (logType) {
      case 'cron_started': return 'CRON START';
      case 'cron_completed': return 'CRON ZAKOŃCZONY';
      case 'cron_failed': return 'CRON BŁĄD';
      case 'cron_skipped': return 'CRON POMINIĘTY';
      default: return logType;
    }
  }

  /**
   * Kolor status badge dla połączenia sync
   */
  getStatusClass(status: string): string {
    switch (status) {
      case 'ok':
      case 'healthy': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'stale': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      case 'error': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'never': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'ok':
      case 'healthy': return 'OK';
      case 'stale': return 'Przestarzałe';
      case 'error': return 'Błąd';
      case 'never': return 'Nigdy';
      default: return status;
    }
  }

  // ==========================================
  // DIAGNOZUJ
  // ==========================================

  toggleDiagnosePanel(): void {
    this.showDiagnosePanel = !this.showDiagnosePanel;
    if (!this.showDiagnosePanel) {
      this.diagnoseLogLines = [];
      this.diagnoseSuccess = null;
    }
  }

  runDiagnose(): void {
    if (!this.diagnoseNip.trim() || this.isDiagnosing) return;

    this.isDiagnosing = true;
    this.diagnoseLogLines = [];
    this.diagnoseSuccess = null;

    this._ksefApiService.diagnoseSync(this.diagnoseNip.trim())
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (response) => {
          this.isDiagnosing = false;
          this.diagnoseSuccess = response.data?.success ?? false;

          const logs = response.data?.logs || [];
          this.diagnoseLogLines = logs.map((l: any) => ({
            level: l.level,
            text: `[${l.timestamp}] ${l.message}`
          }));
        },
        error: (err) => {
          this.isDiagnosing = false;
          this.diagnoseSuccess = false;
          this.diagnoseLogLines = [{ level: 'error', text: err.error?.message || 'Wystąpił błąd podczas diagnostyki' }];
        }
      });
  }

  diagnoseFromTable(nip: string): void {
    this.showDiagnosePanel = true;
    this.diagnoseNip = nip;
    this.runDiagnose();
  }

  getLogLineClass(level: string): string {
    switch (level) {
      case 'success': return 'text-green-400';
      case 'warn': return 'text-amber-400';
      case 'error': return 'text-red-400';
      default: return 'text-gray-300';
    }
  }
}
